import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { useUrlParams } from '../params'
import { useQuery } from '@tanstack/react-query'
import { ApiError, type NodeInfo } from '../../api'
import { POLL_MS, RELAY, ROUTES_PATH, getAsOf, nodePath, relayKeys, routePath, type RelayLink, type RelayStatsPoint, type RelayStatsSeries, type StatsRange } from './api'
import { ago, bps, clock, ms, pct, plural } from './format'
import { LineChart, onAxis } from './LineChart'
import { noteRelayAsOf, noteRelayFailure, type RelayLive } from './live'
import { errorText } from './queries'
import { LINK_REASON, LINK_SPEC } from './states'
import { Empty, ErrorBox, NetPanel, SkeletonRows, StateBadge } from './ui'

/*
 * Node card › Links (proto nodecard/Tables.tsx LinksTab; 04-admin-api.md §5.3):
 * every link with this node at one end — peer, direction (out = this node sends
 * the media), state with its reason, RTT and hop latency p50 / p99, retransmits
 * (the loss estimate), traffic, legs up, routes, the last failure. Live from SSE
 * link / link_removed; without the stream polled every 5 s.
 * A row opens the link panel (?link=<id>): GET /relay/links/{id} and the series
 * /relay/links/{id}/stats?range= (latency, RTT, retransmits, traffic, legs).
 */

const RANGES: StatsRange[] = ['15m', '1h', '6h', '24h', '7d', '30d']
const STEP_MS: Record<RelayStatsSeries['step'], number> = { '5s': 5000, '1m': 60_000, '1h': 3_600_000 }

type Links = { items: RelayLink[]; loading: boolean; error: unknown; refetch: () => unknown; fetching: boolean }

export function NodeLinks({ n, live, links }: { n: NodeInfo; live: RelayLive; links: Links }) {
  const { params, update } = useUrlParams()
  const sel = params.get('link')
  // focus moves to the panel's heading when the operator opens it, not on a page load with ?link=
  const [opened, setOpened] = useState(0)
  const select = (id: string | null) => {
    if (id) setOpened((x) => x + 1)
    update({ link: id })
  }
  if (links.loading) {
    return (
      <NetPanel>
        <SkeletonRows rows={4} cols={8} />
      </NetPanel>
    )
  }
  if (links.error) return <ErrorBox testId="node-links-error" text={errorText(`${RELAY}/links?node=${n.id}`, links.error)} busy={links.fetching} onRetry={() => void links.refetch()} />
  const ls = links.items
  return (
    <div className="flex flex-col gap-4" data-testid="node-links">
      {!ls.length ? (
        <Empty title="No links yet" testId="node-links-empty">
          A link appears when the first relay leg crosses this node and another one{n.status !== 'online' ? ', and while the node is on line' : ''}. Links without legs for an hour leave the
          list.
        </Empty>
      ) : (
        <NetPanel
          pad={false}
          title={
            <span className="flex items-center gap-2">
              <h2 className="text-[15px] font-semibold">Links</h2>
              <span className="num font-mono text-xs text-muted">{ls.length}</span>
            </span>
          }
          aside={<span className="text-xs text-muted">out = this node sends the media · {live.sse === 'open' ? 'live' : 'polling every 5 s'}</span>}
        >
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full min-w-[920px] text-left text-sm" data-testid="node-links-table">
              <thead className="border-b border-line text-xs text-muted">
                <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:font-medium [&>th:first-child]:pl-4 [&>th:last-child]:pr-4">
                  <th scope="col">Peer</th>
                  <th scope="col">Direction</th>
                  <th scope="col">State</th>
                  <th scope="col" className="text-right">RTT p50 / p99</th>
                  <th scope="col" className="text-right">Latency p50 / p99</th>
                  <th scope="col" className="text-right">Retrans</th>
                  <th scope="col" className="text-right">Traffic</th>
                  <th scope="col" className="text-right">Legs</th>
                  <th scope="col" className="text-right">Routes</th>
                  <th scope="col">Last failure</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {ls.map((l) => (
                  <LinkRow key={l.link_id} l={l} n={n} open={sel === l.link_id} onOpen={() => select(sel === l.link_id ? null : l.link_id)} />
                ))}
              </tbody>
            </table>
          </div>
          <ul className="divide-y divide-line md:hidden">
            {ls.map((l) => {
              const out = l.from.id === n.id
              const peer = out ? l.to : l.from
              const down = l.state === 'down'
              return (
                <li key={l.link_id} data-testid={`node-link-card-${l.link_id}`}>
                  <button type="button" onClick={() => select(sel === l.link_id ? null : l.link_id)} aria-expanded={sel === l.link_id} className="flex min-h-[44px] w-full flex-col gap-1.5 px-4 py-3 text-left">
                    <span className="flex items-start justify-between gap-2">
                      <span className="font-mono text-sm font-semibold">
                        {out ? '→' : '←'} {peer.name}
                      </span>
                      <StateBadge spec={LINK_SPEC[l.state]} />
                    </span>
                    {l.state_reason && <span className={`text-xs ${down ? 'text-fail' : 'text-warn'}`}>{LINK_REASON[l.state_reason]}</span>}
                    <span className="num flex flex-wrap gap-x-3 font-mono text-xs text-text-soft">
                      <span>rtt {down ? '—' : `${ms(l.rtt_p50_ms)}/${ms(l.rtt_p99_ms)}`}</span>
                      <span>retr {down ? '—' : pct(l.retrans_pct)}</span>
                      <span>
                        legs {l.legs_up}/{l.legs}
                      </span>
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </NetPanel>
      )}
      {sel && <LinkPanel id={sel} n={n} live={live} focus={opened} onClose={() => select(null)} />}
    </div>
  )
}

function LinkRow({ l, n, open, onOpen }: { l: RelayLink; n: NodeInfo; open: boolean; onOpen: () => void }) {
  const out = l.from.id === n.id
  const peer = out ? l.to : l.from
  const down = l.state === 'down'
  return (
    <tr
      data-testid={`node-link-${l.link_id}`}
      data-state={l.state}
      aria-selected={open}
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen()
        }
      }}
      className={`cursor-pointer outline-none focus-visible:bg-accent/[.06] focus-visible:shadow-focus [&>td]:px-3 [&>td]:py-2 [&>td]:align-top [&>td:first-child]:pl-4 [&>td:last-child]:pr-4 ${open ? 'bg-accent/[.06]' : 'hover:bg-panel'}`}
    >
      <td>
        <Link to={nodePath(peer.id)} onClick={(e) => e.stopPropagation()} className="font-mono text-xs font-semibold hover:underline">
          {peer.name}
        </Link>
        <div className="font-mono text-[11px] text-muted">{peer.region}</div>
      </td>
      <td className="font-mono text-xs whitespace-nowrap text-text-soft">{out ? `out → ${peer.name}` : `in ← ${peer.name}`}</td>
      <td>
        <StateBadge spec={LINK_SPEC[l.state]} />
        {l.state_reason && <div className={`mt-1 text-[11px] ${down ? 'text-fail' : 'text-warn'}`}>{LINK_REASON[l.state_reason]}</div>}
      </td>
      <td className="num text-right font-mono text-xs whitespace-nowrap">{down ? '—' : `${ms(l.rtt_p50_ms)} / ${ms(l.rtt_p99_ms)}`}</td>
      <td className="num text-right font-mono text-xs whitespace-nowrap">{down ? '—' : `${ms(l.lat_p50_ms)} / ${ms(l.lat_p99_ms)}`}</td>
      <td className={`num text-right font-mono text-xs ${(l.retrans_pct ?? 0) >= 1 ? 'text-warn' : ''}`}>{down ? '—' : pct(l.retrans_pct)}</td>
      <td className="num text-right font-mono text-xs whitespace-nowrap">{l.util_bps ? bps(l.util_bps) : '—'}</td>
      <td className={`num text-right font-mono text-xs ${l.legs > l.legs_up ? 'text-warn' : ''}`}>
        {l.legs_up}/{l.legs}
      </td>
      <td className="num text-right font-mono text-xs">{l.routes}</td>
      <td className="text-xs whitespace-nowrap text-muted">
        {l.last_failure ? (
          <span title={`${clock(l.last_failure.at)}: ${l.last_failure.reason}`}>
            {ago(l.last_failure.at)} · {l.last_failure.reason.replace(/_/g, ' ')}
          </span>
        ) : (
          'none'
        )}
      </td>
    </tr>
  )
}

/** The link panel: details and the series of one link. */
export function LinkPanel({ id, n, live, focus, onClose }: { id: string; /** the node whose card shows it: direction relative to it */ n?: NodeInfo; live: RelayLive; focus: number; onClose: () => void }) {
  const [range, setRange] = useState<StatsRange>('15m')
  const head = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    if (focus) head.current?.focus()
  }, [focus])
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && !document.querySelector('[data-testid="totp-dialog"]') && onClose()
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onClose])
  const dpath = `${RELAY}/links/${encodeURIComponent(id)}`
  const detail = useQuery({
    queryKey: relayKeys.link(id),
    queryFn: async () => {
      try {
        const r = await getAsOf<RelayLink & { as_of: string }>(dpath)
        noteRelayAsOf(r.asOf, r.at)
        return r
      } catch (e) {
        noteRelayFailure(e)
        throw e
      }
    },
    retry: (k, e) => !(e instanceof ApiError && e.status < 500) && k < 1,
    refetchInterval: live.sse === 'open' ? false : POLL_MS,
  })
  const spath = `${dpath}/stats?range=${range}`
  const stats = useQuery({
    queryKey: relayKeys.linkStats(id, range),
    queryFn: async () => {
      try {
        const r = await getAsOf<RelayStatsSeries>(spath)
        noteRelayAsOf(r.asOf, r.at)
        return r
      } catch (e) {
        noteRelayFailure(e)
        throw e
      }
    },
    retry: (k, e) => !(e instanceof ApiError && e.status < 500) && k < 1,
    refetchInterval: range === '15m' || range === '1h' ? POLL_MS : 60_000,
  })
  // the stream's view of the link is newer than the panel's GET
  const fromLive = live.sse === 'open' && live.loaded
  const liveLink = live.links.get(id)
  const gone = fromLive ? !liveLink : detail.error instanceof ApiError && detail.error.status === 404
  const l: RelayLink | undefined = liveLink ?? detail.data?.data
  const nameOf = (x: string) => (l?.from.id === x ? l.from.name : l?.to.id === x ? l.to.name : x)
  return (
    <section className="rs-corners rounded-lg border border-accent/60" aria-labelledby={`link-${id}-h`} data-testid="link-panel" data-link={id} data-state={l?.state ?? ''}>
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3">
        <h2 id={`link-${id}-h`} ref={head} tabIndex={-1} className="font-mono text-[15px] font-semibold outline-none">
          {l ? `${l.from.name} → ${l.to.name}` : id}
        </h2>
        <span className="flex items-center gap-2">
          {l && <StateBadge spec={LINK_SPEC[l.state]} testId="link-panel-state" />}
          <button type="button" aria-label="Close the link panel" data-testid="link-panel-close" onClick={onClose} className="grid min-h-9 min-w-9 place-items-center rounded-md border border-line-2 text-muted hover:text-text max-md:min-h-[44px] max-md:min-w-[44px]">
            ✕
          </button>
        </span>
      </header>
      <div className="flex flex-col gap-4 p-4">
        {gone && (
          <p role="status" className="rounded-md border border-line-2 px-3 py-2 text-sm text-text-soft" data-testid="link-panel-gone">
            This link left the live view: it has had no legs for an hour, or one end was removed. Its series stay until the retention ends.
          </p>
        )}
        {!l && detail.isPending && <SkeletonRows rows={3} cols={4} />}
        {!l && detail.error && !gone && <ErrorBox testId="link-panel-error" text={errorText(dpath, detail.error)} busy={detail.isFetching} onRetry={() => void detail.refetch()} />}
        {l && (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-[13px] sm:grid-cols-4" data-testid="link-panel-facts">
            <Fact k="Direction">{!n ? `${l.from.name} sends to ${l.to.name}` : l.from.id === n.id ? `out → ${l.to.name}` : `in ← ${l.from.name}`}</Fact>
            <Fact k="Reason">{l.state_reason ? LINK_REASON[l.state_reason] : '—'}</Fact>
            <Fact k="Since">
              {clock(l.since)} · {ago(l.since)}
            </Fact>
            <Fact k="Legs up">
              {l.legs_up}/{l.legs}
            </Fact>
            <Fact k="RTT p50 / p99">
              {ms(l.rtt_p50_ms)} / {ms(l.rtt_p99_ms)}
            </Fact>
            <Fact k="Latency p50 / p99">
              {ms(l.lat_p50_ms)} / {ms(l.lat_p99_ms)}
            </Fact>
            <Fact k="Retrans / loss est.">
              {pct(l.retrans_pct)} / {pct(l.loss_pct)}
            </Fact>
            <Fact k="Traffic">
              {bps(l.util_bps)}
              {l.capacity_bps ? ` of ${bps(l.capacity_bps)}` : ''}
            </Fact>
            <Fact k="Last failure">{l.last_failure ? `${clock(l.last_failure.at)} · ${l.last_failure.reason.replace(/_/g, ' ')}` : 'none'}</Fact>
            <div className="col-span-2 flex min-w-0 flex-col gap-0.5 sm:col-span-3">
              <dt className="mono-label uppercase">Routes · {l.routes}</dt>
              <dd className="flex flex-wrap gap-x-2 gap-y-1 font-mono text-xs" data-testid="link-panel-routes">
                {l.route_ids.length ? (
                  l.route_ids.map((r) => (
                    <Link key={r} to={routePath(r)} className="text-text-soft underline underline-offset-2">
                      {r}
                    </Link>
                  ))
                ) : (
                  <span className="text-muted">none now</span>
                )}
              </dd>
              {l.routes > 0 && (
                <dd>
                  <Link to={`${ROUTES_PATH}?link=${encodeURIComponent(l.link_id)}`} className="inline-flex items-center text-xs text-text-soft underline underline-offset-2 max-md:min-h-[44px]" data-testid="link-panel-routes-list">
                    Open these routes in Routes →
                  </Link>
                </dd>
              )}
            </div>
          </dl>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line pt-3">
          <h3 className="text-[13px] font-semibold">Series</h3>
          <span role="group" aria-label="Time range" className="flex flex-wrap gap-1">
            {RANGES.map((x) => (
              <button
                key={x}
                type="button"
                aria-pressed={range === x}
                data-testid={`link-range-${x}`}
                onClick={() => setRange(x)}
                className={`num inline-flex min-h-8 items-center rounded-sm border px-2.5 text-xs max-sm:min-h-[44px] ${range === x ? 'border-live/50 bg-live/[.12] text-live' : 'border-line-2 text-muted hover:text-text'}`}
              >
                {x}
              </button>
            ))}
          </span>
        </div>
        {stats.isPending ? (
          <SkeletonRows rows={3} cols={4} />
        ) : stats.error && !stats.data ? (
          <ErrorBox testId="link-stats-error" text={errorText(spath, stats.error)} busy={stats.isFetching} onRetry={() => void stats.refetch()} />
        ) : !stats.data?.data.series[0]?.points.length ? (
          <Empty title={`No data in the last ${range}`} testId="link-stats-empty">
            No reports from either end in this window, or it is older than the retention.
          </Empty>
        ) : (
          <LinkCharts s={stats.data!.data} nameOf={nameOf} range={range} />
        )}
      </div>
    </section>
  )
}

function Fact({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="mono-label uppercase">{k}</dt>
      <dd className="num min-w-0 font-mono text-xs break-words text-text-soft">{children}</dd>
    </div>
  )
}

function LinkCharts({ s, nameOf, range }: { s: RelayStatsSeries; nameOf: (id: string) => string; range: StatsRange }) {
  const series = s.series[0]!
  const ax = onAxis<RelayStatsPoint>(series.points, STEP_MS[s.step])
  const col = (k: keyof RelayStatsPoint) =>
    ax.t.map((_, i) => {
      const v = ax.at(i)?.[k]
      return typeof v === 'number' ? v : null
    })
  const name = `${nameOf(series.from)} → ${nameOf(series.to)}`
  const short = (v: number) => (v >= 1e9 ? `${(v / 1e9).toFixed(1)}G` : v >= 1e6 ? `${Math.round(v / 1e6)}M` : v >= 1e3 ? `${Math.round(v / 1e3)}k` : `${Math.round(v)}`)
  const fails = col('failures')
  const failed = fails.reduce<number>((a, v) => a + (v ?? 0), 0)
  return (
    <div className="flex flex-col gap-3" data-testid="link-charts" data-points={series.points.length} data-step={s.step}>
      <div className="grid gap-x-8 gap-y-6 lg:grid-cols-2">
        <LineChart
          title="Hop latency"
          unit="ms"
          t={ax.t}
          min={0}
          series={[
            { name: 'p50', values: col('lat_p50_ms'), stroke: 'stroke-text' },
            { name: 'p99', values: col('lat_p99_ms'), stroke: 'stroke-muted', dash: '5 3' },
          ]}
          fmt={(v) => `${Math.round(v)}`}
          label={`Hop latency of ${name}, last ${range}`}
          testId="link-chart-latency"
        />
        <LineChart title="RTT" unit="ms" t={ax.t} min={0} series={[{ name: 'rtt', values: col('rtt_ms'), stroke: 'stroke-text' }]} fmt={(v) => `${Math.round(v)}`} label={`RTT of ${name}, last ${range}`} testId="link-chart-rtt" />
        <LineChart
          title="Retransmits"
          unit="%"
          t={ax.t}
          min={0}
          refLine={2}
          refLabel="2% degraded"
          series={[{ name: 'retrans', values: col('retrans_pct'), stroke: 'stroke-text' }]}
          fmt={(v) => v.toFixed(1)}
          label={`Retransmits of ${name}, last ${range}`}
          testId="link-chart-retrans"
        />
        <LineChart
          title="Traffic"
          unit="b/s"
          t={ax.t}
          min={0}
          series={[{ name: 'traffic', values: col('bps'), stroke: 'stroke-text' }]}
          fmt={short}
          label={`Traffic of ${name}, last ${range}`}
          testId="link-chart-bps"
        />
      </div>
      <p className="text-xs text-muted" data-testid="link-charts-note">
        One point per {s.step} · {plural(series.points.length, 'point')} · {failed ? `${plural(failed, 'connection failure')} in the window` : 'no connection failures in the window'}.
      </p>
    </div>
  )
}
